import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import EnvDashboard from './pages/EnvDashboard';
import EnvWorkflowsDetail from './pages/EnvWorkflowsDetail';
import EnvAppInsightsDetail from './pages/EnvAppInsightsDetail';
import EnvServiceBusDetail from './pages/EnvServiceBusDetail';
import EnvE2eDetail from './pages/EnvE2eDetail';
import Docs from './pages/Docs';
import DocPage from './pages/DocPage';
import PlatformDocs from './pages/PlatformDocs';
import Setup from './pages/Setup';
import WorkflowPromotion from './pages/WorkflowPromotion';
import SitePage from './pages/SitePage';
import SitePipelines from './pages/SitePipelines';
import SectionPage from './pages/SectionPage';
import ReleasesPage from './pages/ReleasesPage';
import DataDictionary from './pages/DataDictionary';
import Search from './pages/Search';
import type { Env } from './types';

const ENVS: Env[] = ['test', 'dev', 'uat', 'prod'];

export default function App() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        {ENVS.map((env) => (
          <Route key={env} path={`/${env}`}>
            <Route index element={<EnvDashboard env={env} />} />
            <Route path="workflows" element={<EnvWorkflowsDetail env={env} />} />
            <Route path="app-insights" element={<EnvAppInsightsDetail env={env} />} />
            <Route path="service-bus" element={<EnvServiceBusDetail env={env} />} />
            <Route path="e2e-results" element={<EnvE2eDetail env={env} />} />
          </Route>
        ))}
        <Route path="/docs" element={<Docs />} />
        <Route path="/docs/*" element={<DocPage />} />

        <Route path="/architecture" element={<PlatformDocs platformId="architecture" title="Architecture" />} />
        <Route path="/architecture/*" element={<PlatformDocs platformId="architecture" title="Architecture" />} />
        <Route path="/salesforce" element={<PlatformDocs platformId="salesforce" title="Salesforce" />} />
        <Route path="/salesforce/*" element={<PlatformDocs platformId="salesforce" title="Salesforce" />} />
        <Route path="/azure" element={<PlatformDocs platformId="azure" title="Azure" />} />
        <Route path="/azure/*" element={<PlatformDocs platformId="azure" title="Azure" />} />
        <Route path="/semarchy" element={<PlatformDocs platformId="semarchy" title="Semarchy" />} />
        <Route path="/semarchy/*" element={<PlatformDocs platformId="semarchy" title="Semarchy" />} />
        <Route path="/netsuite" element={<PlatformDocs platformId="netsuite" title="NetSuite" />} />
        <Route path="/netsuite/*" element={<PlatformDocs platformId="netsuite" title="NetSuite" />} />
        <Route path="/creatio" element={<PlatformDocs platformId="creatio" title="Creatio" />} />
        <Route path="/creatio/*" element={<PlatformDocs platformId="creatio" title="Creatio" />} />
        <Route path="/site" element={<SitePage />} />
        <Route path="/site/pipelines" element={<SitePipelines />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/workflow-promotion" element={<WorkflowPromotion />} />

        <Route path="/infrastructure" element={<Navigate to="/infrastructure/test" replace />} />
        <Route
          path="/infrastructure/:env"
          element={
            <SectionPage
              section="infrastructure"
              title="Infrastructure"
              blurb="Landing zone, networking, naming, RBAC, policy. Published per-environment by the synergy-application-repo deploy pipeline."
            />
          }
        />

        <Route path="/application" element={<Navigate to="/application/test" replace />} />
        <Route
          path="/application/:env"
          element={
            <SectionPage
              section="application"
              title="Application"
              blurb="APIs, Logic App workflows, Service Bus topics. Published per-environment by the synergy-application-repo deploy pipeline."
            />
          }
        />

        <Route path="/developer-portal" element={<Navigate to="/developer-portal/test" replace />} />
        <Route
          path="/developer-portal/:env"
          element={
            <SectionPage
              section="developer-portal"
              title="Developer Portal"
              blurb="API catalogue and try-it console, generated from the OpenAPI specs and the live APIM instance per environment."
            />
          }
        />

        <Route path="/documentation" element={<Navigate to="/documentation/test" replace />} />
        <Route
          path="/documentation/:env"
          element={
            <SectionPage
              section="documentation"
              title="Documentation"
              blurb="Workflow specifications, architecture, and operational documentation. DOCX downloads are published per-environment alongside each release."
            />
          }
        />

        <Route path="/environments" element={<Navigate to="/environments/test" replace />} />
        <Route
          path="/environments/:env"
          element={
            <SectionPage
              section="environments"
              title="Environments"
              blurb="Per-estate landing — deployed resources, hostnames and DNS, networking, security posture, pipelines, validation status, and quick links to Azure / ADO."
            />
          }
        />

        <Route path="/releases" element={<Navigate to="/releases/test" replace />} />
        <Route path="/releases/:env" element={<ReleasesPage />} />

        <Route path="/data-dictionary" element={<Navigate to="/data-dictionary/test" replace />} />
        <Route path="/data-dictionary/:env" element={<DataDictionary />} />

        <Route path="/dashboard" element={<Navigate to="/test" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
